import React, { useState } from 'react';
import { useT } from '../../i18n';

// Donut chart for the allocation breakdown.
//
// A donut rather than a pie: the hole carries the total and the hovered slice's
// figures, which is where the eye already is. Slices are ordered by size, so the
// sequence around the ring is itself information.
//
// Colours are a fixed ordered palette rather than generated from the label —
// the same sector must be the same colour every time the page loads, and a hash
// of the name gives you a different one the moment a sector is renamed.

const PALETTE = [
    '#8ffca0', '#0bcbfb', '#ffd08f', '#c792ea', '#ff8f8f',
    '#7fd4c1', '#f7a072', '#9aa5ff', '#e0e0a0', '#6fa8dc',
    '#d98fc4', '#7fc98f',
];

const RADIUS = 90;
const THICKNESS = 34;
const SIZE = 220;

/** Point on the circle at `angle` degrees, measured clockwise from 12 o'clock. */
const point = (angle, radius) => {
    const rad = ((angle - 90) * Math.PI) / 180;
    return [SIZE / 2 + radius * Math.cos(rad), SIZE / 2 + radius * Math.sin(rad)];
};

function arc(startAngle, endAngle) {
    const outer = RADIUS;
    const inner = RADIUS - THICKNESS;
    // A slice covering the whole ring cannot be drawn as one arc — start and
    // end coincide and the path collapses. Two half-arcs draw it correctly.
    if (endAngle - startAngle >= 359.999) {
        const [ox1, oy1] = point(0, outer);
        const [ox2, oy2] = point(180, outer);
        const [ix1, iy1] = point(0, inner);
        const [ix2, iy2] = point(180, inner);
        return [
            `M ${ox1} ${oy1}`,
            `A ${outer} ${outer} 0 1 1 ${ox2} ${oy2}`,
            `A ${outer} ${outer} 0 1 1 ${ox1} ${oy1}`,
            `M ${ix1} ${iy1}`,
            `A ${inner} ${inner} 0 1 0 ${ix2} ${iy2}`,
            `A ${inner} ${inner} 0 1 0 ${ix1} ${iy1}`,
            'Z',
        ].join(' ');
    }

    const large = endAngle - startAngle > 180 ? 1 : 0;
    const [ox1, oy1] = point(startAngle, outer);
    const [ox2, oy2] = point(endAngle, outer);
    const [ix2, iy2] = point(endAngle, inner);
    const [ix1, iy1] = point(startAngle, inner);
    return [
        `M ${ox1} ${oy1}`,
        `A ${outer} ${outer} 0 ${large} 1 ${ox2} ${oy2}`,
        `L ${ix2} ${iy2}`,
        `A ${inner} ${inner} 0 ${large} 0 ${ix1} ${iy1}`,
        'Z',
    ].join(' ');
}

const money = (n) =>
    n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

export default function PieChart({ groups, baseCurrency }) {
    const { t } = useT();
    const [active, setActive] = useState(null);

    const total = groups.reduce((acc, g) => acc + g.value, 0);
    if (total <= 0) return null;

    let angle = 0;
    const slices = groups.map((group, i) => {
        const sweep = (group.value / total) * 360;
        const slice = { ...group, start: angle, end: angle + sweep, colour: PALETTE[i % PALETTE.length] };
        angle += sweep;
        return slice;
    });

    const shown = active === null ? null : slices[active];

    return (
        <div className="pie">
            <svg
                className="pie__svg"
                viewBox={`0 0 ${SIZE} ${SIZE}`}
                role="img"
                aria-label={t('breakdown.chartAria')}
                onMouseLeave={() => setActive(null)}
            >
                {slices.map((slice, i) => (
                    <path
                        key={slice.label}
                        d={arc(slice.start, slice.end)}
                        fill={slice.colour}
                        opacity={active === null || active === i ? 1 : 0.32}
                        onMouseEnter={() => setActive(i)}
                    >
                        <title>{`${slice.label} — ${slice.share.toFixed(1)}%`}</title>
                    </path>
                ))}
                <text className="pie__center_top" x={SIZE / 2} y={SIZE / 2 - 6} textAnchor="middle">
                    {shown ? `${shown.share.toFixed(1)}%` : `${groups.length}`}
                </text>
                <text className="pie__center_sub" x={SIZE / 2} y={SIZE / 2 + 14} textAnchor="middle">
                    {shown ? shown.label : 'groups'}
                </text>
            </svg>

            <ul className="pie__legend">
                {slices.map((slice, i) => (
                    <li
                        key={slice.label}
                        className={`pie__legend_item${active === i ? ' is-active' : ''}`}
                        onMouseEnter={() => setActive(i)}
                        onMouseLeave={() => setActive(null)}
                    >
                        <span className="pie__swatch" style={{ background: slice.colour }} />
                        <span className="pie__legend_label">{slice.label}</span>
                        <span className="pie__legend_share">{slice.share.toFixed(1)}%</span>
                        <span className="pie__legend_value">
                            {money(slice.value)} {baseCurrency}
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
}
